/* eslint-disable max-lines */
import {
    AsyncPipe,
    isPlatformBrowser,
} from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    DOCUMENT,
    HostListener,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    signal,
    ViewChild,
} from "@angular/core";
import {
    takeUntilDestroyed,
    toObservable,
} from "@angular/core/rxjs-interop";
import {
    ActivatedRoute,
    NavigationEnd,
    ParamMap,
    Params,
    Route,
    Router,
    RouterOutlet,
} from "@angular/router";
import {
    CashierTypes,
    getUserProfile,
    LocalStorageService,
    WebSocketProxyServiceV2,
} from "@Front-end/seed";
import { IGetVisitorResponse } from "@Front-end/seed/models/affiliates";
import {
    User,
    UserViewStatuses,
} from "@Front-end/seed/models/user";
import { CashierHandlerDirective } from "@Front-end/seed/modules/cashier";
import { PingDirective } from "@Front-end/seed/modules/ping";
import {
    PromoCodeDirective,
    PromoCodeFacade,
} from "@Front-end/seed/modules/promocode";
import { OtpLinkVerifyDirective } from "@Front-end/seed/modules/quick-sign-up";
import { RegistrationAnalyticInitSources } from "@Front-end/seed/modules/registration-v2";
import { InitTagManagerDirective } from "@Front-end/seed/modules/tag-manager";
import {
    getUser,
    LoadUserDirective,
    UserDirective,
} from "@Front-end/seed/modules/user";
import {
    MissionCompleteDirective,
    MissionCompletePayload,
} from "@Front-end/seed/modules/v2/missions";
import { TournamentFacade } from "@Front-end/seed/modules/v2/tournament";
import { VerifyEmailDirective } from "@Front-end/seed/modules/verify-email";
import {
    AffiliatesService,
    CookieService,
    FeatureService,
    WebSocketProxyService,
} from "@Front-end/seed/services";
import {
    projectParamMap,
    ProjectParams,
} from "@Front-end/seed/utils";
import { ResponseError } from "@Front-end/seed/utils/interfaces";
import { ModalOpenType } from "@Front-end/ui";
import { OnlineStatusDirective } from "@Front-end/ui/directives/online-status";
import { SimpleMap } from "@Front-end/ui/interfaces";
import {
    NotificationAlert,
    NotificationStatuses,
} from "@Front-end/ui/models/notifications";
import {
    NotificationBoxComponent,
    QueueNotificationService,
} from "@Front-end/ui/modules/notifications";
import { LocalizeRouterService } from "@gilsdav/ngx-translate-router";
import Hotjar from "@hotjar/browser";
import {
    select,
    Store,
} from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import {
    catchError,
    combineLatestWith,
    filter,
    map,
    Observable,
    pipe,
    switchMap,
    take,
    tap,
    throwError,
    UnaryFunction,
} from "rxjs";
import { environment } from "../../../../../environments/environment";
import { BottomMenuComponent } from "../../../../components/bottom-menu/bottom-menu.component";
import { MenuComponent } from "../../../../components/menu/menu.component";
import { CashierSources } from "../../../../constants/cashier-source.constants";
import { errorTitle } from "../../../../constants/common.constants";
import { LocalStorageKeys } from "../../../../constants/local-storage-keys.constants";
import { notificationIcons } from "../../../../constants/notification-icons.constants";
import { StorageKeys } from "../../../../constants/storage.constants";
import { LangObj } from "../../../../models/lang.model";
import { RegistrationPopupPayload } from "../../../../models/registration-payload.model";
import { LocalizeService } from "../../../../services/localize.service";
import { MirrorService } from "../../../../services/mirror.service";
import { SeoTagsService } from "../../../../services/seo-tags.service";
import { SidePanelManagerService } from "../../../../services/side-panel-manager.service";
import { AppPopupsService } from "../../../../store/services/app-popups.service";
import { BottomBannerComponent } from "../bottom-banner/bottom-banner.component";
import { CookiesComponent } from "../cookies/cookies.component";
import { FooterComponent } from "../footer/footer.component";
import { HeaderComponent } from "../header/header.component";
import { AddClassEventThemeDirective } from "./directives/add-class-event-theme";

declare global {
    interface Window {
        // For example, if you have a LiveChatWidget property:
        // eslint-disable-next-line @typescript-eslint/naming-convention
        LiveChatWidget?: {
            call: (action: string, data: object | (string | null)) => void;
        }; // Replace "any" with a more specific type if available

        // For analytics (e.g., Sentry or other tracking tools)
        analytics?: {
            identify: (userId: string, data?: Record<string, string | null>) => void;
            page: (data: { name: string; properties?: Record<string, string | null> }) => void;
        };
    }
}


@Component({
    selector: "ls-main",
    imports: [
        UserDirective,
        OtpLinkVerifyDirective,
        LoadUserDirective,
        PingDirective,
        AsyncPipe,
        PromoCodeDirective,
        HeaderComponent,
        RouterOutlet,
        FooterComponent,
        CookiesComponent,
        BottomMenuComponent,
        OnlineStatusDirective,
        NotificationBoxComponent,
        VerifyEmailDirective,
        AddClassEventThemeDirective,
        CashierHandlerDirective,
        InitTagManagerDirective,
        BottomBannerComponent,
        MenuComponent,
        MissionCompleteDirective,
    ],
    templateUrl: "./main.component.html",
    styleUrl: "./main.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "class": "wrapper main-wrapper",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "[class._default-header]": "this.hideHeaderPromo",
    },
  })
export class MainComponent implements OnInit, OnDestroy {
    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) public platformId: object,
        private translate: TranslateService,
        private router: Router,
        private queueNotificationService: QueueNotificationService,
        private changeDetectorRef: ChangeDetectorRef,
        private webSocketService: WebSocketProxyService,
        private route: ActivatedRoute,
        private affiliatesService: AffiliatesService,
        private popupService: AppPopupsService,
        private localizeRouterService: LocalizeRouterService,
        private localizeService: LocalizeService,
        private cookieService: CookieService,
        private promocodeService: PromoCodeFacade,
        private seoTagsService: SeoTagsService,
        private store: Store,
        private mirrorService: MirrorService,
        private sidePanelManagerService: SidePanelManagerService,
        private renderer: Renderer2,
        private featureService: FeatureService, // not delete
        private webSocketProxyServiceV2: WebSocketProxyServiceV2, // not delete
    ) {
        if (this.document.defaultView) {
            this.window = this.document.defaultView;
        }
    }

    @ViewChild(MenuComponent, { static: true })
    public menuComponent!: MenuComponent;

    public hideHeader: boolean = false;
    public hideFooter: boolean = false;
    public hideArguments: boolean = false;
    public hideMenu: boolean = false;
    public hideBottomMenu: boolean = false;
    public isProfilePage: boolean = false;
    public hideHeaderPromo: boolean = false;
    public hideBottomBanner: boolean = false;
    public notificationObservable$?: Observable<NotificationAlert<SimpleMap<string>>[]>;
    public verifyCode: string | null = null;
    public projectParams = ProjectParams;
    public langs: LangObj[] | null = null;
    public notificationIcons = notificationIcons;
    public minioUrl: string = environment.minioUrl;

    private user: User | null = null;
    private paramsToRemove: Record<string, boolean> = {};
    private window!: Window;
    private readonly userStatus = signal<UserViewStatuses | null>(null);
    private userStatus$ = toObservable(this.userStatus);

    private tournamentFacade: TournamentFacade = inject(TournamentFacade);
    private destroyRef = inject(DestroyRef);
    private localStorageService: LocalStorageService = inject(LocalStorageService);

    public ngOnInit(): void {
        // this.handleRecovery();
        /**
         * Handle all notifications
         */
        // @ts-ignore - Type mismatch between notificationObservable$ and getNotificationQueue return type
        this.notificationObservable$ = this.queueNotificationService.getNotificationQueue();
        this.webSocketService.connect();
        this.handleQueryParams();
        this.handleOtpLink();
        this.handleRegistration();
        this.handleDepositParam();
        this.handleLogin();
        this.handlePasswordRecovery();
        this.handlePromocode();
        this.handleWheelOfFortune();
        if (this.document?.defaultView?.localStorage) {
            this.document.defaultView.localStorage.removeItem(StorageKeys.lsWasLoggedIn);
        }
        this.redirectIfInvalidLocale();

        this.langs = environment.local.map((el: string): LangObj => ({ id: el, value: el }));
        this.updateCanonicalMetaTags(this.langs);

        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((event) => {
            if (this.langs) {
                this.updateCanonicalMetaTags(this.langs);
                this.handlePaymentRedirect();
            }
            // Send customer.io info about navigation
            if ((this.document.defaultView as Window) && (this.document.defaultView as Window).analytics) {
                (this.document.defaultView as Window).analytics?.page({
                    name: event.urlAfterRedirects,
                });
            }

        });

        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            combineLatestWith(this.userStatus$),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(([, userStatus]) => {
            if (userStatus === UserViewStatuses.NO_USER) {
                this.handleUserLogin(ProjectParams.AUTO_JOIN_TOURNAMENT);
                this.handleUserLogin(ProjectParams.PROMOCODE_CASHIER);
            }
            if (userStatus === UserViewStatuses.LOADED) {
                this.handlePromocodeCashier();
                this.handJoinTournamentLoggedIn();
            }
        });

        this.seoTagsService.initializeSeo();

        this.store.pipe(
            select(getUser),
            tap((user: User | null) => {
                this.user = user;
                this.setJivoContactInfo();
                // pass userId to Sentry
                if (this.user) {
                    Sentry.setUser({ id: this.user.user_id });
                } else {
                    Sentry.setUser(null);
                }
                // pass userId to hotjar
                const timeoutId = setTimeout(() => {
                    if (Hotjar.isReady()) {
                        Hotjar.identify(this.user?.user_id || null, { email: this.user?.email || "" });
                    }
                    clearTimeout(timeoutId);
                }, 500);
            }),
            filter(Boolean),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(user => {
            // @ts-ignore - analytics is injected by external script and not part of Window type
            (this.document.defaultView as Window).analytics?.identify(user.user_id, {
                email: user.email,
            });
        });
        this.store.pipe(
            select(getUserProfile),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(userProfile => {
            if (userProfile) {
                // LiveChatWidget is injected by external script and not part of Window type
                if ((this.document.defaultView as Window).LiveChatWidget) {
                    // LiveChatWidget.call method is not defined in type
                    (this.document.defaultView as Window).LiveChatWidget?.call("set_session_variables", {
                        // eslint-disable-next-line @typescript-eslint/naming-convention,camelcase
                        user_name: userProfile.name + " " + userProfile.surname,
                    });
                }
            }
        });
        this.reloadOnBfCache();

        // Sentry logger

        // Sentry.logger.info('User triggered test log', { log_source: 'sentry_test' })
    }

    public userStatusChange(status: UserViewStatuses): void {
        this.userStatus.set(status);
        if (status === UserViewStatuses.NO_USER) {
            this.handleUserLogin(ProjectParams.PROMOCODE_CASHIER);
            this.localStorageService.removeItem(LocalStorageKeys.FIRST_DEPOSIT_POPUP);
            this.handleWheelRegistrationWithEmail();
            this.handleWheelRegistrationWithPromoCode();
        }
    }

    /**
     * close all popups if user clicks on browser back button
     */
    @HostListener("window:popstate", ["$event"])
    public onPopState(): void {
        this.popupService.close();
    }

    /**
     * Notification wrapper below
     * @param event NotificationAlert<SimpleMap<string>>
     */
    public handleClose(event: NotificationAlert<SimpleMap<string>>): void {
        this.queueNotificationService.removeNotification(event);
    }

    public userLoaded(): void {
        this.handPromocodeLoggedIn();
        this.handJoinTournamentLoggedIn();
        this.handleCashier();
        this.handlePromocodeCashier();
    }

    public promoCodeActivated(isSuccessful: boolean): void {
        if (isSuccessful) {
            this.queueNotificationService.addNotification({
                label: this.translate.instant("notification.success-title"),
                message: this.translate.instant("notification.success-promocode-activation"),
                status: NotificationStatuses.Success,
            });
        } else {
            this.queueNotificationService.addNotification({
                label: this.translate.instant("notification.warning-title"),
                message: this.translate.instant("notification.failed-promocode-activation"),
                status: NotificationStatuses.Warning,
            });
        }
    }

    public ngOnDestroy(): void {
        this.seoTagsService.seoUnsubscribe();
    }

    public handleStatusChange(isOnline: boolean): void {
        if (!isOnline) {
            this.queueNotificationService.addNotification({
                status: NotificationStatuses.Warning,
                label: this.translate.instant("notification.warning-title"),
                message: this.translate.instant("notification.connection-lost"),
            });
        }
    }

    public handleMissionComplete(mission: MissionCompletePayload): void {
        const rewardTypes: Array<[number | undefined, string]> = [
            [mission.reward.realMoney, "rm.png"],
            [mission.reward.loyaltyPoints, "lp.png"],
            [mission.reward.bonus?.money, "bm.png"],
            [mission.reward.bonus?.freespins, "fs.png"],
            [mission.reward.token?.amount, "spin.png"],
        ];

        const rewardType = rewardTypes.find(([type]) => type)?.[1];

        this.queueNotificationService.addNotification({
            label: this.translate.instant("notification.success-title"),
            status: NotificationStatuses.Success,
            message: this.translate.instant("notification.mission-complete-text", { value: mission.name }),
            ...(rewardType && { imgSrc: `${this.minioUrl}balance/${rewardType}` }),
        });
    }

    public emailVerifySuccess(): void {
        this.queueNotificationService.addNotification({
            status: NotificationStatuses.Success,
            label: this.translate.instant("notification.email-verify-success-title"),
            message: this.translate.instant("notification.email-verify-success-message"),
        });
    }

    public emailVerifyFailed(error: ResponseError): void {
        const expireError: boolean = `${error.code}` === "2000110";
        if (expireError) {
            this.queueNotificationService.addNotification({
                status: NotificationStatuses.Warning,
                label: this.translate.instant("notification.email-verify-failed-title"),
                message: this.translate.instant("notification.email-verify-failed-message"),
            });
        }
        if (!expireError) {
            this.emailVerifySuccess();
        }
    }

    public activate(): void {
        const currentLang = this.getCurrentLocale();
        this.localizeRouterService.changeLanguage(currentLang);
        this.localizeService.updateLocale(currentLang);
        const availableLocals: string[] = environment.local;
        const splittedUrl: string[] = this.router.url
            .split(/[/?]/)
            .filter((part: string) => part && !availableLocals.some((item: string) => item === part));
        let foundPathConfig: Route | undefined;
        let childrenFoundPathConfig: Route | undefined;

        if (splittedUrl.length > 0) {
            const urlPath: string | undefined = splittedUrl[0];
            foundPathConfig = this.router.config.find(
                el => el.path && urlPath && el.path === urlPath,
            );
            childrenFoundPathConfig = this.router.config[0].children?.find(
                el => el.path && urlPath && el.path === urlPath,
            );
        }
        if (foundPathConfig !== undefined) {
            this.hideHeader = foundPathConfig.data?.["hideHeader"];
            this.hideFooter = foundPathConfig.data?.["hideFooter"];
            this.hideArguments = foundPathConfig.data?.["hideArguments"];
            this.hideMenu = foundPathConfig.data?.["hideMenu"];
            this.isProfilePage = foundPathConfig.data?.["isProfilePage"];
            this.hideHeaderPromo = foundPathConfig.data?.["hideHeaderPromo"];
            this.hideBottomBanner = foundPathConfig.data?.["hideBottomBanner"];
        } else if (childrenFoundPathConfig !== undefined) {
            this.hideHeader = childrenFoundPathConfig.data?.["hideHeader"];
            this.hideFooter = childrenFoundPathConfig.data?.["hideFooter"];
            this.hideArguments = childrenFoundPathConfig.data?.["hideArguments"];
            this.hideMenu = childrenFoundPathConfig.data?.["hideMenu"];
            this.hideBottomMenu = childrenFoundPathConfig.data?.["hideBottomMenu"];
            this.isProfilePage = childrenFoundPathConfig.data?.["isProfilePage"];
            this.hideHeaderPromo = childrenFoundPathConfig.data?.["hideHeaderPromo"];
            this.hideBottomBanner = childrenFoundPathConfig.data?.["hideBottomBanner"];
        } else {
            this.hideHeader = false;
            this.hideFooter = false;
            this.hideArguments = false;
            this.hideMenu = false;
            this.hideBottomMenu = false;
            this.isProfilePage = false;
            this.hideHeaderPromo = false;
            this.hideBottomBanner = false;
        }
        // TODO markForCheck() not working, header will not disappear
        this.changeDetectorRef.detectChanges();
    }

    public toggleMenu(): void {
        if (this.sidePanelManagerService.isOpen()) {
            this.sidePanelManagerService.close();
        }

        if (!this.sidePanelManagerService.isOpen()) {
            this.sidePanelManagerService.open();
        }
    }

    private setJivoContactInfo(): void {
        if ((this.document.defaultView as Window).LiveChatWidget) {
            if (this.user) {
                this.document.defaultView?.LiveChatWidget?.call("set_customer_name", this.user.login);
                this.document.defaultView?.LiveChatWidget?.call("set_customer_email", this.user.email);
            }
        }
    }

    /**
     * Append script to head tag loading jivo chat
     * @private
     */
    // private appendJivo(): void {
    //     const node = this.document.createElement("script");
    //     node.src = `//code.jivosite.com/widget/${environment.jivoKey}`;
    //     node.type = "text/javascript";
    //     node.defer = true;
    //     this.document.getElementsByTagName("head")[0].appendChild(node);
    // }

    private handleQueryParams(): void {
        this.route.queryParams.pipe(
            take(1),
            this.removeProjectParams(),
            map((affiliateParams: Record<string, string>) => {

                if (!affiliateParams || !Object.keys(affiliateParams)?.length) {
                    return {};
                }
                let affiliates: Record<string, string> = {};
                let oldAffiliates: Record<string, string> = {};

                if (this.cookieService.getCookie(StorageKeys.affiliate)) {
                    try {
                        oldAffiliates = JSON.parse(this.cookieService.getCookie(StorageKeys.affiliate) as string);
                    } catch (error) {

                        console.warn("Error parsing affiliate cookie:", error);
                    }
                }

                if (oldAffiliates && Object.keys(oldAffiliates).length > 0) {
                    affiliates = { ...oldAffiliates };
                } else {
                    affiliates = { ...affiliateParams };
                    this.cookieService.setCookie(StorageKeys.affiliate, JSON.stringify(affiliates));
                }
                return affiliates;
            }),
            switchMap((affiliates) => this.affiliatesService.receiveUserId(affiliates)),
            tap((result: IGetVisitorResponse) => {
                this.setAffiliatesCookie(result);
            }),
            catchError((error) => throwError(error)),
        ).subscribe();
    }

    private handleOtpLink(): void {
        const paramsMap: ParamMap = this.route.snapshot.queryParamMap;
        if (paramsMap.has(ProjectParams.CODE)) {
            this.verifyCode = paramsMap.get(ProjectParams.CODE);
        }
    }

    private handleCashier(): void {
        const params: Params = this.route.snapshot.queryParams;
        const cashier: string | undefined = params[ProjectParams.CASHIER];
        if (cashier && cashier === "true") {
            this.popupService.fireOpenCashier({
                source: CashierSources.URL_PARAM_CASHIER,
                type: CashierTypes.DEPOSIT,
                openType: ModalOpenType.PROGRAM,
            });
            this.removeParam(params, ProjectParams.CASHIER);
        }
    }

    private handlePromocodeCashier(): void {
        const params: Params = this.route.snapshot.queryParams;
        const promocode: string | undefined = params[ProjectParams.PROMOCODE_CASHIER];
        if (promocode) {
            this.popupService.fireOpenCashier({
                source: CashierSources.URL_PARAM_CASHIER_PROMOCODE,
                type: CashierTypes.DEPOSIT,
                openType: ModalOpenType.PROGRAM,
                promoCode: promocode,
            });
            this.removeParam(params, ProjectParams.PROMOCODE_CASHIER);
        }
    }

    private handleDepositParam(): void {
        const params: Params = this.route.snapshot.queryParams;
        const success: string | undefined = params[ProjectParams.SUCCESS_DEPOSIT];
        const cancel: string | undefined = params[ProjectParams.CANCEL_DEPOSIT];
        const storageUrl = this.document?.defaultView?.localStorage.getItem(StorageKeys.depositSource);

        if (success && success === "true") {
            this.queueNotificationService.addNotification({
                status: NotificationStatuses.Success,
                message: this.translate.instant("notification.success-deposit"),
                label: this.translate.instant("notification.success-deposit-title"),
            });
            if (storageUrl) {
                this.document?.defaultView?.localStorage.removeItem(StorageKeys.depositSource);
            }
            this.removeParam(params, ProjectParams.SUCCESS_DEPOSIT);
        }
        if (cancel && cancel === "true") {
            this.queueNotificationService.addNotification({
                status: NotificationStatuses.Error,
                message: this.translate.instant("notification.cancel-deposit"),
                label: this.translate.instant(errorTitle),
            });
            if (storageUrl) {
                this.document?.defaultView?.localStorage.removeItem(StorageKeys.depositSource);
            }
            this.removeParam(params, ProjectParams.CANCEL_DEPOSIT);
        }
    }

    private setAffiliatesCookie(data: IGetVisitorResponse | undefined): void {
        if (isPlatformBrowser(this.platformId)) {
            if (data) {
                const { cookieData, cookieName, expireDate } = data;
                const expires = new Date(expireDate).toUTCString();
                this.document.cookie = `${cookieName}=${cookieData};expires=${expires};`;
            }
        }
    }

    private handleRegistration(): void {
        const param: ParamMap = this.route.snapshot.queryParamMap;
        const paramStr: string = this.window.location.search;
        if (param.has(ProjectParams.REGISTRATION)) {
            if (param.get(ProjectParams.REGISTRATION) === "true" && !this.document?.defaultView?.localStorage.getItem("lsToken")) {
                this.popupService.fireOpenRegistration({
                    source: RegistrationAnalyticInitSources.URL_PARAMS,
                    urlParams: paramStr,
                }, ModalOpenType.PROGRAM);
            }
        }
    }

    private handleWheelOfFortune(): void {
        const params: Params = this.route.snapshot.queryParams;
        if (params[ProjectParams.WHEEL_OF_FORTUNE] === "true") {
            this.popupService.fireOpenLuckyWheel(ModalOpenType.PROGRAM);
            this.removeParam(params, ProjectParams.WHEEL_OF_FORTUNE);
        }
    }

    private handleWheelRegistrationWithEmail(): void {
        const params: Params = this.route.snapshot.queryParams;
        const paramStr: string = this.window.location.search;
        const email: string | undefined = params[ProjectParams.EMAIL];
        let payload: RegistrationPopupPayload | null = null;
        if (email) {
            payload = {
                contact: email,
                urlParams: paramStr,
                source: RegistrationAnalyticInitSources.URL_PARAMS,
            };
        }
        if (payload) {
            this.popupService.fireOpenRegistration(payload, ModalOpenType.PROGRAM);
            this.removeParam(params, ProjectParams.EMAIL);
        }
    }

    private handleWheelRegistrationWithPromoCode(): void {
        const params: Params = this.route.snapshot.queryParams;
        const paramStr: string = this.window.location.search;
        const promoCode: string | undefined = params[ProjectParams.PROMOCODE];
        let payload: RegistrationPopupPayload | null = null;
        if (promoCode) {
            payload = {
                promoCode: promoCode,
                urlParams: paramStr,
                source: RegistrationAnalyticInitSources.URL_PARAMS,
            };
        }
        if (payload) {
            this.popupService.fireOpenRegistration(payload, ModalOpenType.PROGRAM);
            this.removeParam(params, ProjectParams.PROMOCODE);
        }
    }

    private getCurrentLocale(): string {
        const availableLocals: string[] = environment.local;
        const browserLocale: string | undefined = this.translate.getBrowserCultureLang()?.toLowerCase();
        const storageLang: string | null = isPlatformBrowser(this.platformId) ? localStorage.getItem(StorageKeys.lsLang) : null;
        const urlLocale = this.router.url.split("/")[1];

        let currentLocale: string;
        if (storageLang) {
            currentLocale = storageLang;
        } else if (browserLocale && availableLocals.includes(browserLocale)) {
            currentLocale = browserLocale;
        } else if (urlLocale && availableLocals.includes(urlLocale)) {
            currentLocale = urlLocale;
        } else {
            currentLocale = availableLocals[0];
        }
        this.renderer.setAttribute(this.document.documentElement, "lang", currentLocale);
        return currentLocale;
    }

    private redirectIfInvalidLocale(): void {
        const availableLocals = environment.local;
        const [pathname, queryString] = this.router.url.split("?");
        const urlLocale = pathname.split("/")[1];

        if (!availableLocals.includes(urlLocale)) {
            const currentLocale = this.getCurrentLocale();
            const path = pathname.split("/").slice(2).join("/");
            const basePath = path ? `/${currentLocale}/${path}` : `/${currentLocale}`;
            const queryParams: Record<string, string> = {};
            if (queryString) {
                queryString.split("&").forEach(param => {
                    const [key, value] = param.split("=");
                    if (key && value) {
                        queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
                    }
                });
            }

            void this.router.navigate([basePath], { queryParams });
        }
    }


    private handleLogin(): void {
        const params: Params = this.route.snapshot.queryParams;
        const login: string | undefined = params[ProjectParams.LOGIN];
        if (login && login === "true") {
            this.popupService.fireOpenLogin(ModalOpenType.PROGRAM);
            this.removeParam(params, ProjectParams.LOGIN);
        }
    }

    private handlePasswordRecovery(): void {
        const params: Params = this.route.snapshot.queryParams;
        const recovery: string | undefined = params[ProjectParams.PASSWORD_RECOVERY];
        if (recovery && recovery === "true") {
            this.popupService.fireOpenRecoveryPassword(ModalOpenType.PROGRAM);
            this.removeParam(params, ProjectParams.PASSWORD_RECOVERY);
        }
    }

    private handlePaymentRedirect(): void {
        const params: Params = this.route.snapshot.queryParams;
        const paymentResult: string | undefined = params[ProjectParams.PAYMENT_RESULT];
        paymentResult && this.removeParam(params, ProjectParams.PAYMENT_RESULT);
    }

    private handlePromocode(): void {
        const param: ParamMap = this.route.snapshot.queryParamMap;
        if (param.has(ProjectParams.RETENTION_PROMOCODE)) {
            if (param.get(ProjectParams.RETENTION_PROMOCODE)) {
                this.queueNotificationService.addNotification({
                    status: NotificationStatuses.Success,
                    message: this.translate.instant("notification.retention-text"),
                    label: this.translate.instant("notification.retention-title"),
                });
            }
        }
    }

    private handPromocodeLoggedIn(): void {
        const params: Params = this.route.snapshot.queryParams;
        const promo: string | undefined = params[ProjectParams.RETENTION_PROMOCODE];
        if (promo) {
            this.promocodeService.sendPromoCode(promo);
            this.removeParam(params, ProjectParams.RETENTION_PROMOCODE);
        }
    }

    private handleUserLogin(param: ProjectParams): void {
        const params: Params = this.route.snapshot.queryParams;
        const autoJoin: string | undefined = params[param];
        if (autoJoin) {
            this.popupService.fireOpenLogin(ModalOpenType.PROGRAM);
        }
    }

    private handJoinTournamentLoggedIn(): void {
        const params: Params = this.route.snapshot.queryParams;
        const autoJoin: string | undefined = params[ProjectParams.AUTO_JOIN_TOURNAMENT];
        if (autoJoin) {
            this.removeParam(params, ProjectParams.AUTO_JOIN_TOURNAMENT);
            const tournamentId = Number(location.pathname.split("/").pop());
            if (tournamentId) {
                this.tournamentFacade.joinTournament(tournamentId);
            }
        }
    }

    private removeParam(params: Params, paramToRemove: string): void {
        this.paramsToRemove = { ...this.paramsToRemove, [paramToRemove]: true };
        const paramToKeep: string[] = Object.keys(params).filter(
            (item: string) => !this.paramsToRemove[item],
        );
        const updatedParams: Params = paramToKeep.reduce(
            (res: Params, param: string): Params => ({ ...res, [param]: params[param] }), <Params> {});
        void this.router.navigate([], { queryParams: updatedParams, replaceUrl: true, relativeTo: this.route });
    }

    private removeProjectParams(): UnaryFunction<Observable<Record<string, string>>, Observable<Record<string, string>>> {
        return pipe(
            map((params: Record<string, string>) => {
                let affiliateParams: Record<string, string> = {};
                Object.keys(params).forEach((param: string) => {
                    if (!Object.prototype.hasOwnProperty.call(projectParamMap, param)) {
                        affiliateParams = { ...affiliateParams, [param]: params[param] };
                    }
                });
                return affiliateParams;
            }),
        );
    }

    private updateCanonicalMetaTags(languages: LangObj[]): void {
        if (languages.length > 0) {
            this.updateCanonicalURL(languages);
            this.updateAlternateURLs(languages);
        }
    }

    private updateCanonicalURL(languages: LangObj[]): void {
        let canonicalLink: HTMLLinkElement | null = this.getLink("canonical");
        if (!canonicalLink) {
            canonicalLink = this.createLink("canonical");
        }
        const canonicalURL: string = this.getCanonicalURL(languages);
        canonicalLink.setAttribute("href", canonicalURL);
    }

    private updateAlternateURLs(languages: LangObj[]): void {
        const currentLang: string = this.translate.currentLang;
        const baseUrl: string = `${this.document.location.protocol}//${this.document.location.host}`;

        languages.forEach(lang => {
            let alternateLink: HTMLLinkElement | null = this.getLink("alternate", lang.id);
            if (!alternateLink) {
                alternateLink = this.createLink("alternate", lang.id);
            }
            const alternateURL: string = this.getAlternateURL(currentLang, lang.id, baseUrl);
            alternateLink.setAttribute("href", alternateURL);
        });

        let xDefaultLink: HTMLLinkElement | null = this.getLink("alternate", "x-default");
        if (!xDefaultLink) {
            xDefaultLink = this.createLink("alternate", "x-default");
        }
        xDefaultLink.setAttribute("href", this.mirrorService.toOriginalHost(`${baseUrl}/en`));
    }

    private getLink(rel: string, hreflang?: string): HTMLLinkElement | null {
        return this.document.querySelector(`link[rel="${rel}"]${hreflang ? `[hreflang="${hreflang}"]` : ""}`);
    }

    private createLink(rel: string, hreflang?: string): HTMLLinkElement {
        const link: HTMLLinkElement = this.document.createElement("link");
        link.setAttribute("rel", rel);
        if (hreflang) {
            link.setAttribute("hreflang", hreflang);
        }
        this.document.head.appendChild(link);
        return link;
    }

    private getCanonicalURL(languages: LangObj[]): string {
        const currentLang: string = this.translate.currentLang;
        const baseUrl: string = this.mirrorService.toOriginalHost(
            `${this.document.location.protocol}//${this.document.location.host}`,
        );
        const isGamePage: boolean = this.router.url.includes("/game/");

        if (this.router.url === `/${currentLang}` || this.router.url === `/${currentLang}/`) {
            return `${baseUrl}/${currentLang}`;
        }

        const langPattern: string = languages.map(lang => lang.id).join("|");
        const updatedUrl: string = this.document.location.href.replace(new RegExp(`/(${langPattern})/`), `/${currentLang}/`);
        const canonicalURL: string = this.mirrorService.toOriginalHost(updatedUrl);

        return isGamePage ? canonicalURL : canonicalURL.split("?")[0];
    }

    private getAlternateURL(currentLang: string, langId: string, baseUrl: string): string {
        const isGamePage: boolean = this.router.url.includes("/game/");

        if (currentLang) {
            let alternateURL = this.document.location.href.replace(new RegExp(`/${currentLang}(/|$)`), `/${langId}$1`);
            alternateURL = this.mirrorService.toOriginalHost(alternateURL);
            return isGamePage ? alternateURL : alternateURL.split("?")[0];
        }

        return `${baseUrl}/${langId}`;
    }

    private reloadOnBfCache(): void {
        this.window.onpageshow = (event): void => {
            if (event.persisted) {
                this.window.location.reload();
            }
        };
    }
}
